/**
 * AdminBilling
 *
 * Three-tab admin page for managing Stripe activity:
 *   1. Summary   — aggregate revenue, platform fees, payouts, refunds
 *   2. Refunds   — paid lessons that were cancelled/expired and need (or got) a refund
 *   3. Payouts   — completed lessons awaiting (or already sent) coach payout
 *
 * Data is read entirely from the `lessons` Firestore collection.
 * Actions call the existing `refundLesson` and `transferToCoach` Cloud Functions.
 */

import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { getStripeAdminLessons, getTrainingProgramRefundRequests, getRefundedOrders, getPaidOrders, getAllUsers, type StripeAdminLesson, type TrainingProgramRefundRequest, type OrderRefundRecord } from '../../services/admin';
import { refundLessonFn, transferToCoachFn, payCoachReferralFn, adminRefreshStripeAccountStatusFn } from '../../services/stripe';
import { adminGrantCreditsFn, getAllCreditGrants, getTotalCreditsIssuedCents, getPlayerCreditBalance } from '../../services/credits';
import type { PlayerCreditTransaction } from '../../types/credits';
import { markOrderRefundIssued } from '../../services/orders';
import { getOrCreateSupportConversation, sendMessage } from '../../services/chat';
import { DOUBLE_BAGEL_UID, DOUBLE_BAGEL_NAME } from '../../constants';
import { useToast } from '../../hooks/useToast';
import {
  getAllPlayerReferralPayouts,
  markPlayerReferralPayoutPaid,
  getAllCoachReferralSubmissions,
  approveCoachReferralSubmission,
  rejectCoachReferralSubmission,
  setCoachReferralSlot,
  getAllCoachReferralRelationships,
  getAllCoachReferralPayouts,
} from '../../services/referrals';
import type { PlayerReferralPayout, CoachReferralPayout, CoachReferralSubmission, CoachReferralRelationship } from '../../types/referrals';
import type { UserProfile } from '../../types/user';

// ── Constants ─────────────────────────────────────────────────────────────────

const PLATFORM_COMMISSION = 0.25;

/** Lesson statuses that indicate a lesson was never delivered */
const UNDELIVERED_STATUSES = new Set([
  'cancelled',
  'deadline_missed',
  'expired_acceptance',
  'expired_submission',
  'declined',
]);

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatCents(cents: number | undefined): string {
  if (!cents) return '—';
  return `$${(cents / 100).toFixed(2)}`;
}

function toMs(v: unknown): number {
  if (v instanceof Object && 'toMillis' in v) return (v as { toMillis(): number }).toMillis();
  return new Date(v as string).getTime();
}

function formatDate(v: unknown): string {
  if (!v) return '—';
  const ms = v instanceof Object && 'toMillis' in v
    ? (v as { toMillis(): number }).toMillis()
    : new Date(v as string).getTime();
  if (!ms || isNaN(ms)) return '—';
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 px-5 py-4">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${color ?? 'text-gray-900'}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function PaymentBadge({ status }: { status?: string }) {
  const map: Record<string, string> = {
    captured:   'bg-blue-100 text-blue-700',
    transferred:'bg-green-100 text-green-700',
    refunded:   'bg-red-100 text-red-700',
    authorized: 'bg-yellow-100 text-yellow-700',
    unpaid:     'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${map[status ?? ''] ?? 'bg-gray-100 text-gray-500'}`}>
      {status ?? 'none'}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const color = UNDELIVERED_STATUSES.has(status)
    ? 'bg-red-50 text-red-700'
    : status === 'completed'
      ? 'bg-green-50 text-green-700'
      : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${color}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

type Tab = 'summary' | 'refunds' | 'payouts' | 'referrals' | 'credits';

export default function AdminBilling() {
  const [tab, setTab] = useState<Tab>('summary');
  const [lessons, setLessons] = useState<StripeAdminLesson[]>([]);
  const [refundRequests, setRefundRequests] = useState<TrainingProgramRefundRequest[]>([]);
  const [refundedOrders, setRefundedOrders] = useState<OrderRefundRecord[]>([]);
  const [paidOrders, setPaidOrders] = useState<import('../../services/admin').PaidOrderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [coachStripeEnabled, setCoachStripeEnabled] = useState<Record<string, boolean>>({});
  const { showSuccess, showError } = useToast();

  // ── Referral state ────────────────────────────────────────────────────────────
  const [playerPayouts, setPlayerPayouts] = useState<PlayerReferralPayout[]>([]);
  const [coachReferralPayouts, setCoachReferralPayouts] = useState<CoachReferralPayout[]>([]);
  const [coachSubmissions, setCoachSubmissions] = useState<CoachReferralSubmission[]>([]);
  const [coachRelationships, setCoachRelationships] = useState<CoachReferralRelationship[]>([]);
  const [coaches, setCoaches] = useState<UserProfile[]>([]);
  const [nameById, setNameById] = useState<Record<string, string>>({});
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [approveCoachId, setApproveCoachId] = useState('');

  // ── Credits state ──────────────────────────────────────────────────────────────
  const [creditsIssuedCents, setCreditsIssuedCents] = useState(0);
  const [creditGrants, setCreditGrants] = useState<PlayerCreditTransaction[]>([]);
  const [grantPlayerId, setGrantPlayerId] = useState('');
  const [grantAmountDollars, setGrantAmountDollars] = useState('');
  const [grantReason, setGrantReason] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantPlayerBalance, setGrantPlayerBalance] = useState<number | null>(null);

  const loadReferrals = useCallback(async () => {
    const [payouts, coachPayouts, submissions, relationships, coachList] = await Promise.all([
      getAllPlayerReferralPayouts(),
      getAllCoachReferralPayouts(),
      getAllCoachReferralSubmissions(),
      getAllCoachReferralRelationships(),
      getAllUsers('coach'),
    ]);
    setPlayerPayouts(payouts);
    setCoachReferralPayouts(coachPayouts);
    setCoachSubmissions(submissions);
    setCoachRelationships(relationships);
    setCoaches(coachList as unknown as UserProfile[]);

    // Build a uid → display name map for all IDs shown in the referrals tab.
    const names: Record<string, string> = {};
    for (const c of coachList as unknown as UserProfile[]) {
      names[c.uid] = c.coachProfile?.displayName || c.displayName || c.email || c.uid;
    }
    // Fetch display names for referred players (not in the coaches list).
    const playerIds = [...new Set(payouts.map((p) => p.referredPlayerId))].filter((id) => !names[id]);
    if (playerIds.length > 0) {
      const snaps = await Promise.all(playerIds.map((id) => getDoc(doc(db, 'users', id))));
      for (const snap of snaps) {
        if (snap.exists()) {
          const d = snap.data();
          names[snap.id] = d.playerProfile?.displayName || d.displayName || snap.id;
        }
      }
    }
    setNameById(names);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lessonData, refundData, refundOrderData, paidOrderData, totalIssuedCents, grantHistory] = await Promise.all([
        getStripeAdminLessons(),
        getTrainingProgramRefundRequests(),
        getRefundedOrders(),
        getPaidOrders(),
        getTotalCreditsIssuedCents().catch((err): 0 => { console.error('[Billing] credits issued load error:', err); return 0; }),
        getAllCreditGrants(100).catch((err): PlayerCreditTransaction[] => { console.error('[Billing] grant history load error:', err); return []; }),
      ]);
      setLessons(lessonData);
      setRefundRequests(refundData);
      setRefundedOrders(refundOrderData);
      setPaidOrders(paidOrderData);
      setCreditsIssuedCents(totalIssuedCents);
      setCreditGrants(grantHistory);
      // Primary data is ready — clear spinner so all tabs are visible immediately.
      // The Stripe refresh below is slow on staging (one CF call per coach in payout
      // queue); it updates coachStripeEnabled in the background without blocking the UI.
      setLoading(false);

      // Refresh Stripe Connect status for coaches in the pending payout queue
      // so the admin always sees live data rather than potentially-stale Firestore values.
      try {
        const queueCoachIds = [...new Set(
          lessonData
            .filter(l => l.status === 'delivered' && l.paymentStatus === 'captured')
            .map(l => l.coachId)
        )];
        const refreshResults = await Promise.allSettled(
          queueCoachIds.map(id => adminRefreshStripeAccountStatusFn({ coachId: id }))
        );
        const stripeMap: Record<string, boolean> = {};
        queueCoachIds.forEach((id, i) => {
          const result = refreshResults[i];
          if (result.status === 'fulfilled') {
            stripeMap[id] = result.value.data.payoutsEnabled;
          } else {
            // Refresh failed — fall back to Firestore value read below
            stripeMap[id] = false;
          }
        });
        // Fill in fallback Firestore values for any coaches whose refresh failed
        const fallbackIds = queueCoachIds.filter((_id, i) => refreshResults[i].status === 'rejected');
        if (fallbackIds.length > 0) {
          const fallbackSnaps = await Promise.all(fallbackIds.map(id => getDoc(doc(db, 'users', id))));
          fallbackSnaps.forEach(snap => {
            if (snap.exists()) stripeMap[snap.id] = snap.data()?.coachProfile?.stripePayoutsEnabled === true;
          });
        }
        setCoachStripeEnabled(stripeMap);
      } catch (stripeErr) {
        console.error('[Billing] Stripe refresh error:', stripeErr);
      }
    } catch (err) {
      console.error('[Billing] load error:', err);
      showError('Failed to load billing data');
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadReferrals(); }, [loadReferrals]);

  // ── Referral action handlers ──────────────────────────────────────────────────

  const handlePayCoachReferral = async (payoutId: string) => {
    setActionLoading(p => ({ ...p, [`crp-${payoutId}`]: true }));
    try {
      await payCoachReferralFn({ payoutId });
      showSuccess('Coach referral payout sent');
      await loadReferrals();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to send payout');
    } finally {
      setActionLoading(p => ({ ...p, [`crp-${payoutId}`]: false }));
    }
  };

  const handleMarkPayoutPaid = async (payoutId: string) => {
    setActionLoading(p => ({ ...p, [payoutId]: true }));
    try {
      await markPlayerReferralPayoutPaid(payoutId);
      showSuccess('Marked as paid');
      await loadReferrals();
    } catch {
      showError('Failed to mark payout paid');
    } finally {
      setActionLoading(p => ({ ...p, [payoutId]: false }));
    }
  };

  const handleToggleSlot = async (coachId: string, current: boolean) => {
    setActionLoading(p => ({ ...p, [`slot-${coachId}`]: true }));
    try {
      await setCoachReferralSlot(coachId, !current);
      showSuccess(!current ? 'Referral slot granted' : 'Referral slot revoked');
      await loadReferrals();
    } catch {
      showError('Failed to update referral slot');
    } finally {
      setActionLoading(p => ({ ...p, [`slot-${coachId}`]: false }));
    }
  };

  const handleApproveSubmission = async (submissionId: string) => {
    if (!approveCoachId) { showError('Select the referred coach account first'); return; }
    setActionLoading(p => ({ ...p, [submissionId]: true }));
    try {
      await approveCoachReferralSubmission(submissionId, approveCoachId, 'admin');
      showSuccess('Submission approved — revenue share relationship created');
      setApproveCoachId('');
      await loadReferrals();
    } catch {
      showError('Failed to approve submission');
    } finally {
      setActionLoading(p => ({ ...p, [submissionId]: false }));
    }
  };

  const handleRejectSubmission = async (submissionId: string) => {
    if (!rejectReason.trim()) { showError('Enter a rejection reason first'); return; }
    setActionLoading(p => ({ ...p, [submissionId]: true }));
    try {
      await rejectCoachReferralSubmission(submissionId, rejectReason.trim(), 'admin');
      showSuccess('Submission rejected');
      setRejectingId(null);
      setRejectReason('');
      await loadReferrals();
    } catch {
      showError('Failed to reject submission');
    } finally {
      setActionLoading(p => ({ ...p, [submissionId]: false }));
    }
  };

  // ── Derived datasets ────────────────────────────────────────────────────────

  /** Lessons that went through the payment flow */
  const paidLessons = lessons.filter(
    (l) => ['captured', 'transferred', 'refunded', 'authorized'].includes(l.paymentStatus ?? '')
  );

  // Summary stats — order-level so training programs count once at full bundle price
  const totalCollectedCents = paidOrders
    .filter((o) => ['captured', 'transferred', 'refunded'].includes(o.paymentStatus))
    .reduce((s, o) => s + o.totalCents, 0);

  const transferredLessons = paidLessons.filter((l) => l.paymentStatus === 'transferred');
  const netCollectedCents = paidOrders
    .filter((o) => ['captured', 'transferred'].includes(o.paymentStatus))
    .reduce((s, o) => s + o.totalCents, 0);
  const platformFeesCents = Math.round(netCollectedCents * PLATFORM_COMMISSION);
  const coachPayoutsCents = paidOrders
    .filter((o) => o.paymentStatus === 'transferred')
    .reduce((s, o) => s + Math.round(o.totalCents * (1 - PLATFORM_COMMISSION)), 0);

  // Lesson IDs blocked from payout: those belonging to orders with a pending or issued refund.
  const refundBlockedLessonIds = new Set(
    refundRequests.flatMap((r) => r.lessonIds)
  );

  // For training programs: lesson 1 is held until lesson 2 has been submitted.
  // Detect training program orders by whether any other lesson shares the same orderId.
  const ordersWithMultipleLessons = new Set(
    Object.entries(
      paidLessons.reduce<Record<string, number>>((acc, l) => {
        if (l.orderId) acc[l.orderId] = (acc[l.orderId] ?? 0) + 1;
        return acc;
      }, {})
    )
      .filter(([, count]) => count > 1)
      .map(([orderId]) => orderId)
  );

  const ordersWithLesson2Submitted = new Set(
    paidLessons
      .filter((l) => (l.lessonNumber ?? 0) >= 2 && l.orderId)
      .map((l) => l.orderId) as string[]
  );

  const pendingPayoutLessons = paidLessons.filter((l) => {
    if (l.status !== 'delivered' || l.paymentStatus !== 'captured') return false;
    if (refundBlockedLessonIds.has(l.id)) return false;
    // Hold lesson 1 of training programs until lesson 2 has been submitted.
    // Only applies to multi-lesson orders — single/shot_consult orders are never held.
    if (
      l.lessonNumber === 1 &&
      l.orderId &&
      ordersWithMultipleLessons.has(l.orderId) &&
      !ordersWithLesson2Submitted.has(l.orderId)
    ) return false;
    return true;
  });
  const pendingPayoutCents = pendingPayoutLessons.reduce(
    (s, l) => s + Math.round((l.priceCents ?? 0) * (1 - PLATFORM_COMMISSION)), 0
  );

  // Refund totals and history are order-based (actuals), not per-lesson
  const refundedCents = refundedOrders.reduce((s, o) => s + o.totalCents, 0);

  /** Refund candidates: paid but lesson undelivered */
  const refundCandidates = paidLessons.filter(
    (l) =>
      UNDELIVERED_STATUSES.has(l.status) &&
      ['captured', 'authorized'].includes(l.paymentStatus ?? '')
  );

  /** Refund history — order-level, already sorted by getRefundedOrders */
  const refundHistory = refundedOrders;

  /** Payout queue: completed, captured (not yet paid out) */
  const payoutQueue = pendingPayoutLessons.sort((a, b) =>
    toMs(b.deliveredAt ?? b.createdAt) - toMs(a.deliveredAt ?? a.createdAt)
  );

  /** Payout history */
  const payoutHistory = transferredLessons.sort((a, b) =>
    toMs(b.deliveredAt ?? b.createdAt) - toMs(a.deliveredAt ?? a.createdAt)
  );

  // ── Actions ─────────────────────────────────────────────────────────────────

  async function handleTrainingProgramRefund(req: TrainingProgramRefundRequest) {
    const lessonId = req.lessonIds[0];
    if (!lessonId) { showError('No lesson found for this refund request'); return; }
    setActionLoading(p => ({ ...p, [req.id]: true }));
    try {
      const result = await refundLessonFn({ lessonId });
      if (result.data.message === 'Already refunded') {
        showSuccess('Already refunded — marking as issued');
      } else {
        showSuccess(`Refund issued (${result.data.refundId})`);
      }
      await markOrderRefundIssued(req.id);
      await load();
    } catch (err: unknown) {
      showError((err as Error)?.message ?? 'Refund failed');
    } finally {
      setActionLoading(p => ({ ...p, [req.id]: false }));
    }
  }

  async function handleRefund(lesson: StripeAdminLesson) {
    setActionLoading((p) => ({ ...p, [lesson.id]: true }));
    try {
      const result = await refundLessonFn({ lessonId: lesson.id });
      if (result.data.message === 'Already refunded') {
        showSuccess('Already refunded — refreshing');
      } else if (result.data.creditRefundTransactionId) {
        showSuccess('Credits restored to player balance');
      } else {
        showSuccess(`Refund issued (${result.data.refundId})`);
      }
      await load();
    } catch (err: unknown) {
      showError((err as Error)?.message ?? 'Refund failed');
    } finally {
      setActionLoading((p) => ({ ...p, [lesson.id]: false }));
    }
  }

  async function handlePayout(lesson: StripeAdminLesson) {
    setActionLoading((p) => ({ ...p, [lesson.id]: true }));
    try {
      const result = await transferToCoachFn({ lessonId: lesson.id });
      if (result.data.message === 'Already transferred') {
        showSuccess('Already transferred — refreshing');
      } else {
        showSuccess(`Payout sent (${result.data.transferId})`);
      }
      await load();
    } catch (err: unknown) {
      showError((err as Error)?.message ?? 'Payout failed');
    } finally {
      setActionLoading((p) => ({ ...p, [lesson.id]: false }));
    }
  }

  async function handleNudge(lesson: StripeAdminLesson) {
    setActionLoading((p) => ({ ...p, [lesson.id]: true }));
    try {
      const coachAmt = lesson.priceCents
        ? `$${(Math.round(lesson.priceCents * (1 - PLATFORM_COMMISSION)) / 100).toFixed(2)}`
        : 'an amount';
      const conversationId = await getOrCreateSupportConversation(lesson.coachId, lesson.coachName);
      const text =
        `Hi ${lesson.coachName}! You have ${coachAmt} pending from a completed lesson. ` +
        `To receive your payout, please set up your Stripe Connect account in the Double Bagel app under Account → Earnings → Set Up Payouts. ` +
        `Once complete, we'll transfer your earnings right away.`;
      await sendMessage(conversationId, DOUBLE_BAGEL_UID, text, 'text', undefined, undefined, { id: DOUBLE_BAGEL_UID, name: DOUBLE_BAGEL_NAME });
      showSuccess(`Nudge sent to ${lesson.coachName}`);
    } catch (err: unknown) {
      showError((err as Error)?.message ?? 'Failed to send nudge');
    } finally {
      setActionLoading((p) => ({ ...p, [lesson.id]: false }));
    }
  }

  // ── Credits actions ────────────────────────────────────────────────────────────

  async function handleGrantCredits() {
    const amountCents = Math.round(parseFloat(grantAmountDollars) * 100);
    if (!grantPlayerId.trim() || isNaN(amountCents) || amountCents <= 0) {
      showError('Enter a valid player UID and amount.');
      return;
    }
    setGrantLoading(true);
    try {
      const { data } = await adminGrantCreditsFn({ playerId: grantPlayerId.trim(), amountCents, reason: grantReason.trim() || undefined });
      showSuccess(`Granted $${(amountCents / 100).toFixed(2)} — new balance: $${(data.newBalanceCents / 100).toFixed(2)}`);
      setGrantPlayerBalance(data.newBalanceCents);
      setGrantAmountDollars('');
      setGrantReason('');
      // Refresh grant history
      getAllCreditGrants(100).then(setCreditGrants).catch(() => {});
      getTotalCreditsIssuedCents().then(setCreditsIssuedCents).catch(() => {});
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to grant credits');
    } finally {
      setGrantLoading(false);
    }
  }

  async function handleLookupBalance() {
    if (!grantPlayerId.trim()) return;
    try {
      const bal = await getPlayerCreditBalance(grantPlayerId.trim());
      setGrantPlayerBalance(bal);
    } catch {
      setGrantPlayerBalance(null);
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  const pendingPlayerPayouts = playerPayouts.filter(p => p.status === 'pending');
  const pendingCoachReferralPayouts = coachReferralPayouts.filter(p => p.status === 'pending');
  const pendingCoachSubmissions = coachSubmissions.filter(s => s.status === 'pending');

  const tabs: { id: Tab; label: string; badge?: number }[] = [
    { id: 'summary', label: 'Summary' },
    { id: 'refunds', label: 'Refunds', badge: refundCandidates.length + refundRequests.length },
    { id: 'payouts', label: 'Coach Payouts', badge: payoutQueue.length },
    { id: 'referrals', label: 'Referrals', badge: pendingPlayerPayouts.length + pendingCoachReferralPayouts.length + pendingCoachSubmissions.length || undefined },
    { id: 'credits', label: 'Credits' },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-gray-900">Billing &amp; Payments</h1>
        <p className="text-sm text-gray-500 mt-0.5">Stripe account summary, refund processing, and coach payouts</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            data-testid={`billing-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-tennis-green text-tennis-green'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
            {!!t.badge && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold">
                {t.badge}
              </span>
            )}
          </button>
        ))}
        <div className="ml-auto flex items-center pb-2">
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
          >
            <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24" data-testid="billing-loading-spinner">
          <div className="w-8 h-8 border-2 border-gray-200 border-t-tennis-green rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* ── Tab 1: Summary ── */}
          {tab === 'summary' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <StatCard
                  label="Total Collected"
                  value={formatCents(totalCollectedCents)}
                  sub={`${paidLessons.filter((l) => ['captured','transferred','refunded'].includes(l.paymentStatus ?? '')).length} lessons`}
                />
                <StatCard
                  label="Platform Fees"
                  value={formatCents(platformFeesCents)}
                  sub="25% of collected"
                  color="text-tennis-green"
                />
                <StatCard
                  label="Coach Payouts"
                  value={formatCents(coachPayoutsCents)}
                  sub={`${transferredLessons.length} transferred`}
                  color="text-blue-600"
                />
                <StatCard
                  label="Pending Payouts"
                  value={formatCents(pendingPayoutCents)}
                  sub={`${pendingPayoutLessons.length} awaiting`}
                  color={pendingPayoutLessons.length > 0 ? 'text-orange-500' : 'text-gray-900'}
                />
                <StatCard
                  label="Total Refunded"
                  value={formatCents(refundedCents)}
                  sub={`${refundedOrders.length} refunds`}
                  color={refundedOrders.length > 0 ? 'text-red-500' : 'text-gray-900'}
                />
                <StatCard
                  label="Credits Issued"
                  value={formatCents(creditsIssuedCents)}
                  sub="Admin grants (all-time)"
                  color={creditsIssuedCents > 0 ? 'text-purple-600' : 'text-gray-900'}
                />
              </div>

              {/* Breakdown table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h2 className="text-sm font-semibold text-gray-700">Payment Status Breakdown</h2>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="px-5 py-2.5 text-left font-medium">Status</th>
                      <th className="px-5 py-2.5 text-right font-medium">Count</th>
                      <th className="px-5 py-2.5 text-right font-medium">Total Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(['captured', 'transferred', 'refunded', 'authorized', 'unpaid'] as const).map((ps) => {
                      // For refunded: use order-level actuals (one order per refund, may cover multiple lessons)
                      if (ps === 'refunded') {
                        if (refundedOrders.length === 0) return null;
                        return (
                          <tr key={ps}>
                            <td className="px-5 py-3"><PaymentBadge status={ps} /></td>
                            <td className="px-5 py-3 text-right text-gray-700 font-medium">{refundedOrders.length}</td>
                            <td className="px-5 py-3 text-right text-gray-700 font-mono">{formatCents(refundedCents)}</td>
                          </tr>
                        );
                      }
                      const rows = paidLessons.filter((l) => (l.paymentStatus ?? 'unpaid') === ps);
                      if (rows.length === 0) return null;
                      const total = rows.reduce((s, l) => s + (l.priceCents ?? 0), 0);
                      return (
                        <tr key={ps}>
                          <td className="px-5 py-3"><PaymentBadge status={ps} /></td>
                          <td className="px-5 py-3 text-right text-gray-700 font-medium">{rows.length}</td>
                          <td className="px-5 py-3 text-right text-gray-700 font-mono">{formatCents(total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Action items callout */}
              {(refundCandidates.length > 0 || payoutQueue.length > 0) && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl px-5 py-4 space-y-2">
                  <p className="text-sm font-semibold text-orange-800">Action Required</p>
                  {refundCandidates.length > 0 && (
                    <p className="text-sm text-orange-700">
                      • {refundCandidates.length} lesson{refundCandidates.length > 1 ? 's' : ''} need{refundCandidates.length === 1 ? 's' : ''} a refund (paid but undelivered)
                    </p>
                  )}
                  {payoutQueue.length > 0 && (
                    <p className="text-sm text-orange-700">
                      • {payoutQueue.length} completed lesson{payoutQueue.length > 1 ? 's' : ''} awaiting coach payout
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Tab 2: Refunds ── */}
          {tab === 'refunds' && (
            <div className="space-y-6">

              {/* Training program refund requests — player-initiated, awaiting admin action */}
              {refundRequests.length > 0 && (
                <div className="bg-white rounded-xl border border-orange-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-orange-100 flex items-center justify-between bg-orange-50">
                    <div>
                      <h2 className="text-sm font-semibold text-orange-800">Training Program Refund Requests</h2>
                      <p className="text-xs text-orange-600 mt-0.5">Player-requested refunds awaiting admin review</p>
                    </div>
                    <span className="text-xs font-medium text-orange-700 bg-orange-100 border border-orange-300 px-2 py-0.5 rounded-full">
                      {refundRequests.length} pending
                    </span>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-5 py-2.5 text-left font-medium">Player → Coach</th>
                        <th className="px-4 py-2.5 text-left font-medium">Program</th>
                        <th className="px-4 py-2.5 text-left font-medium">Amount</th>
                        <th className="px-4 py-2.5 text-left font-medium">Reason</th>
                        <th className="px-4 py-2.5 text-left font-medium">Requested</th>
                        <th className="px-4 py-2.5 text-right font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {refundRequests.map((req) => (
                        <tr key={req.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <p className="font-medium text-gray-800">{req.playerName}</p>
                            <p className="text-xs text-gray-400">{req.coachName}</p>
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate">{req.offeringTitle ?? '—'}</td>
                          <td className="px-4 py-3 font-mono font-medium text-gray-800">{formatCents(req.priceCents * (req.totalLessons ?? 1))}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs max-w-[180px]">{req.refundRequestReason ?? '—'}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(req.refundRequestedAt)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              data-testid="issue-refund-btn"
                              onClick={() => handleTrainingProgramRefund(req)}
                              disabled={actionLoading[req.id]}
                              className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                            >
                              {actionLoading[req.id] ? 'Processing…' : 'Issue Refund'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pending refunds */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-700">Pending Refunds</h2>
                    <p className="text-xs text-gray-400 mt-0.5">Paid lessons that were never delivered</p>
                  </div>
                  {refundCandidates.length > 0 && (
                    <span className="text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                      {refundCandidates.length} pending
                    </span>
                  )}
                </div>
                {refundCandidates.length === 0 ? (
                  <div className="px-5 py-10 text-center text-sm text-gray-400">No pending refunds</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-5 py-2.5 text-left font-medium">Player → Coach</th>
                        <th className="px-4 py-2.5 text-left font-medium">Lesson</th>
                        <th className="px-4 py-2.5 text-left font-medium">Amount</th>
                        <th className="px-4 py-2.5 text-left font-medium">Lesson Status</th>
                        <th className="px-4 py-2.5 text-left font-medium">Payment</th>
                        <th className="px-4 py-2.5 text-left font-medium">Date</th>
                        <th className="px-4 py-2.5 text-right font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {refundCandidates.map((l) => (
                        <tr key={l.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <p className="font-medium text-gray-800">{l.playerName}</p>
                            <p className="text-xs text-gray-400">{l.coachName}</p>
                          </td>
                          <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate">{l.offeringTitle ?? '—'}</td>
                          <td className="px-4 py-3 font-mono font-medium text-gray-800">{formatCents(l.priceCents)}</td>
                          <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                          <td className="px-4 py-3"><PaymentBadge status={l.paymentStatus} /></td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(l.createdAt)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              data-testid="issue-refund-btn"
                              onClick={() => handleRefund(l)}
                              disabled={actionLoading[l.id]}
                              className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                            >
                              {actionLoading[l.id] ? 'Processing…' : 'Issue Refund'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Refund history */}
              {refundHistory.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-700">Refund History</h2>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-5 py-2.5 text-left font-medium">Player → Coach</th>
                        <th className="px-4 py-2.5 text-left font-medium">Amount</th>
                        <th className="px-4 py-2.5 text-left font-medium">Offering</th>
                        <th className="px-4 py-2.5 text-left font-medium">Refund ID</th>
                        <th className="px-4 py-2.5 text-left font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {refundHistory.map((o) => (
                        <tr key={o.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3">
                            <p className="font-medium text-gray-800">{o.playerName}</p>
                            <p className="text-xs text-gray-400">{o.coachName}</p>
                          </td>
                          <td className="px-4 py-3 font-mono font-medium text-gray-800">{formatCents(o.totalCents)}</td>
                          <td className="px-4 py-3 text-xs text-gray-500">{o.offeringTitle ?? '—'}</td>
                          <td className="px-4 py-3 text-xs text-gray-400 font-mono">{o.refundId ?? '—'}</td>
                          <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(o.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Tab 3: Coach Payouts ── */}
          {tab === 'payouts' && (
            <div className="space-y-6">
              {/* Payout queue */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-semibold text-gray-700">Awaiting Payout</h2>
                    <p className="text-xs text-gray-400 mt-0.5">Completed lessons where the coach has not yet been paid</p>
                  </div>
                  {payoutQueue.length > 0 && (
                    <span className="text-xs font-medium text-orange-600 bg-orange-50 border border-orange-200 px-2 py-0.5 rounded-full">
                      {payoutQueue.length} pending · {formatCents(pendingPayoutCents)} total
                    </span>
                  )}
                </div>
                {payoutQueue.length === 0 ? (
                  <div className="px-5 py-10 text-center text-sm text-gray-400">No payouts pending</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-5 py-2.5 text-left font-medium">Coach → Player</th>
                        <th className="px-4 py-2.5 text-left font-medium">Lesson</th>
                        <th className="px-4 py-2.5 text-left font-medium">Charged</th>
                        <th className="px-4 py-2.5 text-left font-medium">Coach Receives (75%)</th>
                        <th className="px-4 py-2.5 text-left font-medium">Completed</th>
                        <th className="px-4 py-2.5 text-right font-medium">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {payoutQueue.map((l) => {
                        const coachAmt = l.priceCents ? Math.round(l.priceCents * (1 - PLATFORM_COMMISSION)) : undefined;
                        return (
                          <tr key={l.id} className="hover:bg-gray-50">
                            <td className="px-5 py-3">
                              <p className="font-medium text-gray-800">{l.coachName}</p>
                              <p className="text-xs text-gray-400">{l.playerName}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-600 max-w-[160px]">
                              <span className="truncate block">{l.offeringTitle ?? '—'}</span>
                              {l.paymentMethod === 'credits' && (
                                <span className="inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-purple-100 text-purple-700">DB-Funded</span>
                              )}
                            </td>
                            <td className="px-4 py-3 font-mono font-medium text-gray-800">{formatCents(l.priceCents)}</td>
                            <td className="px-4 py-3 font-mono font-semibold text-blue-700">{formatCents(coachAmt)}</td>
                            <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(l.deliveredAt)}</td>
                            <td className="px-4 py-3 text-right">
                              {coachStripeEnabled[l.coachId] ? (
                                <button
                                  data-testid="send-payout-btn"
                                  onClick={() => handlePayout(l)}
                                  disabled={actionLoading[l.id]}
                                  className="px-3 py-1.5 text-xs font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                >
                                  {actionLoading[l.id] ? 'Processing…' : 'Send Payout'}
                                </button>
                              ) : (
                                <button
                                  data-testid="nudge-coach-btn"
                                  onClick={() => handleNudge(l)}
                                  disabled={actionLoading[l.id]}
                                  className="px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors"
                                >
                                  {actionLoading[l.id] ? 'Sending…' : 'Nudge Coach'}
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Payout history */}
              {payoutHistory.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="px-5 py-3 border-b border-gray-100">
                    <h2 className="text-sm font-semibold text-gray-700">Payout History</h2>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-5 py-2.5 text-left font-medium">Coach → Player</th>
                        <th className="px-4 py-2.5 text-left font-medium">Charged</th>
                        <th className="px-4 py-2.5 text-left font-medium">Paid Out (75%)</th>
                        <th className="px-4 py-2.5 text-left font-medium">Platform Fee (25%)</th>
                        <th className="px-4 py-2.5 text-left font-medium">Transfer ID</th>
                        <th className="px-4 py-2.5 text-left font-medium">Completed</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {payoutHistory.map((l) => {
                        const coachAmt = l.priceCents ? Math.round(l.priceCents * (1 - PLATFORM_COMMISSION)) : undefined;
                        const fee = l.priceCents ? Math.round(l.priceCents * PLATFORM_COMMISSION) : undefined;
                        return (
                          <tr key={l.id} className="hover:bg-gray-50">
                            <td className="px-5 py-3">
                              <p className="font-medium text-gray-800">{l.coachName}</p>
                              <p className="text-xs text-gray-400">{l.playerName}</p>
                            </td>
                            <td className="px-4 py-3 font-mono text-gray-800">{formatCents(l.priceCents)}</td>
                            <td className="px-4 py-3 font-mono font-semibold text-blue-700">{formatCents(coachAmt)}</td>
                            <td className="px-4 py-3 font-mono text-tennis-green">{formatCents(fee)}</td>
                            <td className="px-4 py-3 text-xs text-gray-400 font-mono">{l.transferId ?? '—'}</td>
                            <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(l.deliveredAt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {/* ── Tab 4: Referrals ── */}
          {tab === 'referrals' && (
            <div className="space-y-8">

              {/* Player referral payouts */}
              <section>
                <h2 className="text-base font-semibold text-gray-800 mb-3">Player Referral Payouts</h2>
                {playerPayouts.length === 0 ? (
                  <p className="text-sm text-gray-400">No player referral payouts yet.</p>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Referring Coach</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Referred Player</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Amount</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Created</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                          <th className="px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {playerPayouts.map((payout) => (
                          <tr key={payout.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-gray-800">{nameById[payout.referringCoachId] ?? '—'}</p>
                              <p className="font-mono text-[10px] text-gray-400">{payout.referringCoachId}</p>
                            </td>
                            <td className="px-4 py-3">
                              <p className="text-sm font-medium text-gray-800">{nameById[payout.referredPlayerId] ?? '—'}</p>
                              <p className="font-mono text-[10px] text-gray-400">{payout.referredPlayerId}</p>
                            </td>
                            <td className="px-4 py-3 font-semibold">${(payout.amountCents / 100).toFixed(2)}</td>
                            <td className="px-4 py-3 text-xs text-gray-400">{new Date(payout.createdAt).toLocaleDateString()}</td>
                            <td className="px-4 py-3">
                              <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                payout.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                              }`}>{payout.status}</span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              {payout.status === 'pending' && (
                                <button
                                  data-testid={`mark-paid-${payout.id}`}
                                  onClick={() => handleMarkPayoutPaid(payout.id)}
                                  disabled={actionLoading[payout.id]}
                                  className="px-3 py-1 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
                                >
                                  Mark paid
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Coach-on-coach referral payouts */}
              <section>
                <h2 className="text-base font-semibold text-gray-800 mb-3">Coach Referral Payouts</h2>
                {coachReferralPayouts.length === 0 ? (
                  <p className="text-sm text-gray-400">No coach referral payouts yet.</p>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Referring Coach</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Lesson</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Gross</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Payout</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                          <th className="px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {coachReferralPayouts.map((p) => {
                          const loadKey = `crp-${p.id}`;
                          return (
                            <tr key={p.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-gray-800">{nameById[p.referringCoachId] ?? '—'}</p>
                                <p className="font-mono text-[10px] text-gray-400">{p.referringCoachId}</p>
                              </td>
                              <td className="px-4 py-3 font-mono text-xs text-gray-400">{p.lessonId}</td>
                              <td className="px-4 py-3 text-xs">${(p.grossCents / 100).toFixed(2)}</td>
                              <td className="px-4 py-3 font-semibold">${(p.payoutCents / 100).toFixed(2)} <span className="text-xs font-normal text-gray-400">({(p.rate * 100).toFixed(0)}%)</span></td>
                              <td className="px-4 py-3 text-xs text-gray-400">{new Date(p.createdAt).toLocaleDateString()}</td>
                              <td className="px-4 py-3">
                                <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                  p.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                }`}>{p.status}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                {p.status === 'pending' && (
                                  <button
                                    onClick={() => handlePayCoachReferral(p.id)}
                                    disabled={actionLoading[loadKey]}
                                    className="px-3 py-1 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
                                  >
                                    {actionLoading[loadKey] ? 'Paying…' : 'Pay out'}
                                  </button>
                                )}
                                {p.status === 'paid' && p.transferId && (
                                  <span className="font-mono text-[10px] text-gray-400">{p.transferId}</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Coach referral slot management */}
              <section>
                <h2 className="text-base font-semibold text-gray-800 mb-3">Coach Referral Slots</h2>
                {coaches.length === 0 ? (
                  <p className="text-sm text-gray-400">No coaches found.</p>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Coach</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Slot</th>
                          <th className="px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {coaches.map((coach) => {
                          const hasSlot = coach.coachProfile?.hasReferralSlot ?? false;
                          const loadKey = `slot-${coach.uid}`;
                          return (
                            <tr key={coach.uid} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <p className="font-medium text-gray-800">{coach.coachProfile?.displayName || coach.displayName || coach.uid}</p>
                                <p className="text-xs text-gray-400">{coach.email}</p>
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                  hasSlot ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                }`}>{hasSlot ? 'Active' : 'None'}</span>
                              </td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => handleToggleSlot(coach.uid, hasSlot)}
                                  disabled={actionLoading[loadKey]}
                                  className="px-3 py-1 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 transition-colors"
                                >
                                  {hasSlot ? 'Revoke' : 'Grant'}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              {/* Coach-on-coach submissions */}
              <section>
                <h2 className="text-base font-semibold text-gray-800 mb-3">Coach Referral Submissions</h2>
                {coachSubmissions.length === 0 ? (
                  <p className="text-sm text-gray-400">No submissions yet.</p>
                ) : (
                  <div className="space-y-3">
                    {coachSubmissions.map((sub) => (
                      <div key={sub.id} data-testid={`submission-card-${sub.id}`} className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="font-semibold text-gray-800">{sub.candidateName}</p>
                            <p className="text-xs text-gray-500">{sub.candidateEmail}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              Referred by: <span className="font-medium text-gray-700">{nameById[sub.referringCoachId] ?? '—'}</span>
                              <span className="font-mono text-[10px] ml-1">({sub.referringCoachId})</span>
                            </p>
                          </div>
                          <span className={`flex-shrink-0 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                            sub.status === 'pending' ? 'bg-amber-100 text-amber-700'
                            : sub.status === 'accepted' ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                          }`}>{sub.status === 'pending' ? 'Under review' : sub.status === 'accepted' ? 'Accepted' : 'Rejected'}</span>
                        </div>
                        {sub.credentials && (
                          <p className="text-xs text-gray-600"><span className="font-medium">Credentials:</span> {sub.credentials}</p>
                        )}
                        {sub.fitNotes && (
                          <p className="text-xs text-gray-600"><span className="font-medium">Fit notes:</span> {sub.fitNotes}</p>
                        )}
                        {sub.status === 'pending' && (
                          <div className="pt-1 space-y-2">
                            <div className="flex gap-2">
                              <select
                                value={approveCoachId}
                                onChange={e => setApproveCoachId(e.target.value)}
                                className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-400"
                              >
                                <option value="">Select referred coach account…</option>
                                {coaches.map(c => (
                                  <option key={c.uid} value={c.uid}>
                                    {c.coachProfile?.displayName || c.displayName || c.uid}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => handleApproveSubmission(sub.id)}
                                disabled={actionLoading[sub.id] || !approveCoachId}
                                className="px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
                              >
                                Approve
                              </button>
                            </div>
                            {rejectingId === sub.id ? (
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  value={rejectReason}
                                  onChange={e => setRejectReason(e.target.value)}
                                  placeholder="Rejection reason…"
                                  className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-1.5 focus:outline-none focus:border-red-400"
                                />
                                <button
                                  onClick={() => handleRejectSubmission(sub.id)}
                                  disabled={actionLoading[sub.id]}
                                  className="px-3 py-1.5 text-xs font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 transition-colors"
                                >
                                  Confirm
                                </button>
                                <button onClick={() => { setRejectingId(null); setRejectReason(''); }} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-800">
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setRejectingId(sub.id)}
                                className="px-3 py-1.5 text-xs font-medium border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                              >
                                Reject
                              </button>
                            )}
                          </div>
                        )}
                        {sub.status === 'rejected' && sub.rejectionReason && (
                          <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{sub.rejectionReason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Coach referral relationships */}
              <section>
                <h2 className="text-base font-semibold text-gray-800 mb-3">Active Revenue-Share Relationships</h2>
                {coachRelationships.length === 0 ? (
                  <p className="text-sm text-gray-400">No relationships yet.</p>
                ) : (
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Referrer</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Referred Coach</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Rate</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Window ends</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Earned</th>
                          <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {coachRelationships.map((rel) => {
                          const windowEnd = new Date(rel.overrideWindowEndsAt ?? rel.windowEndsAt);
                          const daysLeft = Math.max(0, Math.ceil((windowEnd.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));
                          const effectiveRate = rel.overrideRate ?? rel.revenueShareRate;
                          return (
                            <tr key={rel.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-gray-800">{nameById[rel.referringCoachId] ?? '—'}</p>
                                <p className="font-mono text-[10px] text-gray-400">{rel.referringCoachId}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm font-medium text-gray-800">{nameById[rel.referredCoachId] ?? '—'}</p>
                                <p className="font-mono text-[10px] text-gray-400">{rel.referredCoachId}</p>
                              </td>
                              <td className="px-4 py-3">{(effectiveRate * 100).toFixed(0)}%</td>
                              <td className="px-4 py-3 text-xs text-gray-500">
                                {windowEnd.toLocaleDateString()}
                                {rel.status === 'active' && <span className="ml-1 text-gray-400">({daysLeft}d left)</span>}
                              </td>
                              <td className="px-4 py-3 font-semibold">${((rel.totalEarnedCents ?? 0) / 100).toFixed(2)}</td>
                              <td className="px-4 py-3">
                                <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                                  rel.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                                }`}>{rel.status}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

            </div>
          )}

          {/* ── Tab 5: Credits ── */}
          {tab === 'credits' && (
            <div className="space-y-6">
              {/* Grant credits form */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100">
                  <h2 className="text-sm font-semibold text-gray-700">Grant Credits to Player</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Credits cover full lesson/program cost at checkout. No Stripe charge issued.</p>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label className="text-xs text-gray-500 mb-1 block">Player UID</label>
                      <input
                        data-testid="credit-grant-player-uid"
                        type="text"
                        value={grantPlayerId}
                        onChange={e => { setGrantPlayerId(e.target.value); setGrantPlayerBalance(null); }}
                        onBlur={handleLookupBalance}
                        placeholder="Firebase UID"
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <div className="w-36">
                      <label className="text-xs text-gray-500 mb-1 block">Amount (USD)</label>
                      <input
                        data-testid="credit-grant-amount"
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={grantAmountDollars}
                        onChange={e => setGrantAmountDollars(e.target.value)}
                        placeholder="e.g. 50.00"
                        className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Reason (optional)</label>
                    <input
                      data-testid="credit-grant-reason"
                      type="text"
                      value={grantReason}
                      onChange={e => setGrantReason(e.target.value)}
                      placeholder="e.g. Goodwill credit, promotional grant…"
                      className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      data-testid="credit-grant-submit-btn"
                      onClick={handleGrantCredits}
                      disabled={grantLoading || !grantPlayerId.trim() || !grantAmountDollars}
                      className="px-4 py-2 text-sm font-medium bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-40 transition-colors"
                    >
                      {grantLoading ? 'Granting…' : 'Grant Credits'}
                    </button>
                    {grantPlayerBalance !== null && (
                      <span data-testid="credit-grant-balance-display" className="text-sm text-gray-600">
                        Current balance: <span className="font-semibold text-purple-700">${(grantPlayerBalance / 100).toFixed(2)}</span>
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Recent grants list */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-gray-700">Recent Admin Grants</h2>
                  <span className="text-xs text-purple-600 font-medium">Total: {formatCents(creditsIssuedCents)}</span>
                </div>
                {creditGrants.length === 0 ? (
                  <div className="px-5 py-10 text-center text-sm text-gray-400">No credits granted yet</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wide">
                        <th className="px-5 py-2.5 text-left font-medium">Player ID</th>
                        <th className="px-4 py-2.5 text-left font-medium">Amount</th>
                        <th className="px-4 py-2.5 text-left font-medium">Balance After</th>
                        <th className="px-4 py-2.5 text-left font-medium">Reason</th>
                        <th className="px-4 py-2.5 text-left font-medium">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {creditGrants.map((g) => (
                        <tr key={g.id} className="hover:bg-gray-50">
                          <td className="px-5 py-3 font-mono text-xs text-gray-500">{g.playerId}</td>
                          <td className="px-4 py-3 font-mono font-semibold text-purple-700">{formatCents(g.amountCents)}</td>
                          <td className="px-4 py-3 font-mono text-gray-700">{formatCents(g.balanceAfterCents)}</td>
                          <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{g.reason ?? '—'}</td>
                          <td className="px-4 py-3 text-xs text-gray-400">{formatDate(g.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

        </>
      )}
    </div>
  );
}
